import { cva, type VariantProps } from 'class-variance-authority';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Delta } from '@music-vine/cadence/ui';
import { cn } from '@/lib/utils';
import type { FAQs as FAQsType } from '@/types/referAndEarn';
import { SimpleMarkdown } from './SimpleMarkdown';
import { CustomLicensingFormTrigger } from '../PricingPage/CustomLicensingFormTrigger';
const faqsVariants = cva('w-full', {
  variants: {
    variant: {
      default: 'self-center md:max-w-[480px] xl:max-w-[600px] 2xl:max-w-[800px]',
      inline: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const faqsContentVariants = cva('prose-sm w-full md:max-w-[480px] xl:max-w-[600px] 2xl:max-w-[800px]', {
  variants: {
    variant: {
      // No px of its own: AccordionContent already carries the same
      // `md:px-2` the trigger does, so anything here offsets the answer
      // from its question (DEV-6728).
      default: 'self-center',
      inline: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const faqsContainerVariants = cva('flex flex-col', {
  variants: {
    variant: {
      default: 'my-5 mb-10 flex flex-col md:my-10 md:mb-20',
      inline: 'my-5 md:mt-10'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
interface FAQProps extends VariantProps<typeof faqsVariants> {
  centeredTitle?: boolean;
  className?: string;
  faqs: FAQsType;
  title?: string | false; // false to hide title
}
const FAQs = ({
  faqs,
  title = 'Frequently asked questions',
  variant,
  className,
  centeredTitle = false
}: FAQProps) => <section className={cn(faqsContainerVariants({
  variant
}), className)} data-sentry-component="FAQs" data-sentry-source-file="index.tsx">
    {!!title && (variant === 'inline' ? <Delta asChild>
          <h2 className={cn('fl-mb-4/6', centeredTitle && 'text-center')}>
            {title}
          </h2>
        </Delta> : <h2 className={cn('fl-mb-4/12 font-semibold text-2xl 2xl:text-3xl', centeredTitle && 'text-center')}>
          {title}
        </h2>)}
    <Accordion className={cn(faqsVariants({
    variant
  }))} collapsible schemaContent={faqs.map(faq => ({
    header: faq.question,
    text: faq.answer
  }))} type="single" data-sentry-element="Accordion" data-sentry-source-file="index.tsx">
      {faqs.map((faq, index) => <AccordionItem key={faq.question} value={faq.question}>
          <AccordionTrigger>{faq.question}</AccordionTrigger>
          <AccordionContent className="pt-1 pb-4">
            <SimpleMarkdown className={cn(faqsContentVariants({
          variant
        }))}>
              {faq.answer}
            </SimpleMarkdown>
            {!!faq.showCustomLicensingButton && <div className="mt-4 flex justify-center">
                <CustomLicensingFormTrigger content="Get in touch about custom licensing"
          /*
           * Give each event a custom value for GA tracking.
           * Uses index + 1 so that the events are more readable to non-devs.
           */ eventValue={`faq-${index + 1}`} type="button" />
              </div>}
          </AccordionContent>
        </AccordionItem>)}
    </Accordion>
  </section>;
export default FAQs;